import { ReactNode } from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { AlertCircle, AlertTriangle, CheckCircle, Info } from "lucide-react";

import { cn } from "@/lib/utils";

const calloutVariants = cva("rounded-lg border p-4", {
  variants: {
    variant: {
      info: "border-blue-200 bg-blue-50",
      warning: "border-yellow-200 bg-yellow-50",
      error: "border-red-200 bg-red-50",
      success: "border-green-200 bg-green-50",
    },
    size: {
      sm: "p-3",
      md: "p-4",
      lg: "p-5",
    },
  },
  defaultVariants: {
    variant: "info",
    size: "md",
  },
});

const iconVariants = cva("flex-shrink-0", {
  variants: {
    variant: {
      info: "text-blue-600",
      warning: "text-yellow-600",
      error: "text-red-400",
      success: "text-green-600",
    },
    size: {
      sm: "h-3.5 w-3.5",
      md: "h-4 w-4",
      lg: "h-5 w-5",
    },
  },
  defaultVariants: {
    variant: "info",
    size: "md",
  },
});

const textVariants = cva("", {
  variants: {
    variant: {
      info: "text-blue-800",
      warning: "text-yellow-800",
      error: "text-red-800",
      success: "text-green-800",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
      semibold: "font-semibold",
      bold: "font-bold",
    },
    size: {
      sm: "text-xs",
      md: "text-sm",
      lg: "text-base",
    },
  },
  defaultVariants: {
    variant: "info",
    weight: "normal",
    size: "md",
  },
});

const defaultIcons = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  success: CheckCircle,
} as const;

interface CalloutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof calloutVariants> {
  children: ReactNode;
  icon?: ReactNode;
  title?: string;
  weight?: VariantProps<typeof textVariants>["weight"];
}

export function Callout({
  className,
  variant = "info",
  size = "md",
  weight = "normal",
  children,
  icon,
  title,
  ...props
}: CalloutProps) {
  const IconComponent = defaultIcons[variant || "info"];
  const displayIcon =
    icon !== undefined ? (
      icon
    ) : (
      <IconComponent className={iconVariants({ variant, size })} />
    );

  return (
    <div
      className={cn(calloutVariants({ variant, size }), className)}
      {...props}
    >
      <div className="flex gap-3">
        <div className="flex h-5 mt-0.5">{displayIcon}</div>
        <div className="flex-1">
          {title && (
            <p
              className={cn(
                textVariants({ variant, weight: "medium", size }),
                "mb-1",
              )}
            >
              {title}
            </p>
          )}
          <div className={cn(textVariants({ variant, weight, size }))}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
